import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material';
import { interval, Subscription, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { passwordMatchValidator } from '../shared/validators';

import { SEND_CODE_ATTEMPT_COUNTDOWN_FROM } from './constants';
import { FormDataType } from './types';

@Component({
  selector: 'app-password-recovery',
  templateUrl: './password-recovery.component.html',
  styleUrls: ['./password-recovery.component.scss'],
})
export class PasswordRecoveryComponent {
  /** Текущее значение обратного отсчета */
  countdownValue = 0;

  /** Флаг, что всплывающая подсказка видна */
  codeHintVisible = false;

  /** Флаг, что обратный отсчет начался */
  isCountdownStarted = false;

  private countdown$?: Subscription;

  passwordHide = true;

  codeFieldVisible = false;

  form = new FormGroup(
    {
      numberLS: new FormControl(undefined, [Validators.required]),
      password: new FormControl(undefined, [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$'),
      ]),
      confirmPassword: new FormControl(undefined, [Validators.required]),
      code: new FormControl(undefined, [Validators.required]),
    },
    { validators: [passwordMatchValidator] },
  );

  constructor(
    private http: HttpClient,
    private router: Router,
    private readonly snackBar: MatSnackBar,
  ) {}

  get numberLSControl() {
    return this.form.get('numberLS');
  }

  get numberLSValue() {
    return this.numberLSControl.value;
  }

  get passwordControl() {
    return this.form.get('password');
  }

  get passwordValue() {
    return this.passwordControl.value;
  }

  get confirmPasswordControl() {
    return this.form.get('confirmPassword');
  }

  get confirmPasswordValue() {
    return this.confirmPasswordControl.value;
  }

  get codeControl() {
    return this.form.get('code');
  }

  get codeValue() {
    return this.codeControl.value;
  }

  getCode() {
    const { numberLS } = this.form.getRawValue() as FormDataType;
    this.countdown();

    this.http
      .get(`/api/token/newCode?kod_lsch=${numberLS}`)
      .pipe(
        catchError(error => {
          this.snackBar.open(error.error, 'Ошибка', {
            duration: 5000,
          });

          return throwError(() => error);
        }),
      )
      .subscribe(() => {
        this.codeFieldVisible = true;
      });
  }

  onClickSaveNewPasswordButton() {
    const { numberLS, password, code } = this.form.value as FormDataType;

    this.http
      .post(`/api/token/newPassword`, { login: numberLS, password, code })
      .pipe(
        catchError(error => {
          this.snackBar.open(error.error, 'Ошибка', {
            duration: 5000,
          });

          return throwError(() => error);
        }),
      )
      .subscribe(() => {
        this.router.navigateByUrl('/');
      });
  }

  private resetCountdown(): void {
    if (this.countdown$) {
      this.countdown$.unsubscribe();
    }
  }

  /**
   * Таймер обратного отсчета, когда можно будет отправить код повторно
   * */
  private countdown(): void {
    this.resetCountdown();
    this.countdownValue = SEND_CODE_ATTEMPT_COUNTDOWN_FROM;
    this.isCountdownStarted = true;

    this.countdown$ = interval(1000)
      .pipe(take(SEND_CODE_ATTEMPT_COUNTDOWN_FROM))
      .subscribe({
        next: () => {
          this.countdownValue -= 1;
        },
        complete: () => {
          this.isCountdownStarted = false;
        },
      });
  }
}
